import { useState, useCallback } from 'react';
import { QueryKey } from 'react-query';

import { OperationState } from '../../../../../../../main/operations/operationsTypes';

import {
  UniversalFilterOperationStateValue,
  UniversalFilterOperationStateModes,
  UniversalFilterOperationStateFilterState,
  UniversalFilterOperationStateOnFilterStateChange,
  UniversalFilterOperationStateOnChange
} from '../../UniversalFilterOperationState.types';

import { useMountEffect } from '../../../../../../../common/hooks/useMountEffect';
import { useReactQueryState } from '../../../../../../../main/common/reactQuery/hooks/useReactQueryState';

import { useUniversalFilterOperationStateFilterState } from '../useUniversalFilterOperationStateFilterState';

import {
  UniversalFilterTemplateModeOption,
  UniversalFilterTemplateOnModeChange
} from '../../../../helpers/UniversalFilterTemplate';

import { filtersKeys } from '../../../../../../../locales/keys';

const defaultMode = UniversalFilterOperationStateModes.IS_ANY_OF;

const modeOptions: UniversalFilterTemplateModeOption<UniversalFilterOperationStateModes>[] =
  [
    {
      value: UniversalFilterOperationStateModes.IS_ANY_OF,
      i18nText: filtersKeys.modes.isAnyOf
    },
    {
      value: UniversalFilterOperationStateModes.IS_NONE_OF,
      i18nText: filtersKeys.modes.isNoneOf
    }
  ];

interface UniversalFilterOperationStateOptions {
  filterStateCacheKey: QueryKey;
  filterValue?: UniversalFilterOperationStateValue;
  onChange: UniversalFilterOperationStateOnChange;
}

function useUniversalFilterOperationState({
  filterStateCacheKey,
  filterValue,
  onChange
}: UniversalFilterOperationStateOptions) {
  const { value: savedState, setValue: saveState } =
    useReactQueryState<UniversalFilterOperationStateFilterState>(
      filterStateCacheKey
    );

  const { handleApplyFilterState, handleParseFilterValue } =
    useUniversalFilterOperationStateFilterState({
      onChange
    });

  const [filterState, setFilterState] =
    useState<UniversalFilterOperationStateFilterState>(
      savedState ||
        handleParseFilterValue(filterValue) || {
          mode: defaultMode,
          states: []
        }
    );

  useMountEffect(() => {
    handleApplyFilterState(filterState);
  });

  const handleFilterStateChange =
    useCallback<UniversalFilterOperationStateOnFilterStateChange>(
      (state) => {
        setFilterState(state);
        handleApplyFilterState(state);
        saveState(state);
      },
      [handleApplyFilterState, saveState]
    );

  const handleModeChange = useCallback<
    UniversalFilterTemplateOnModeChange<UniversalFilterOperationStateModes>
  >(
    (mode) => {
      handleFilterStateChange({
        mode,
        states: filterState.states || []
      });
    },
    [filterState, handleFilterStateChange]
  );

  const handleStatusesChange = useCallback<(states: OperationState[]) => void>(
    (states) => {
      handleFilterStateChange({
        mode: filterState.mode,
        states: states || []
      });
    },
    [filterState, handleFilterStateChange]
  );

  return {
    modeOptions,
    filterState,
    handleModeChange,
    handleStatusesChange
  };
}

export default useUniversalFilterOperationState;
